import { useEffect, useState } from "react";
import { itx, noItx } from "../../translationItx/itx";
import { EventKey } from "../../eventLogger/EventKeys";

import BaseDialog from "../../modelDialogs/BaseDialog";
import { ChannelType, DeviceType, getChannelTypePicture } from "../../models/ChannelType";
import Logger from "../../Logger";
import Floor from "../../models/Floor";
import Room from "../../models/Room";
import Cabinet from "../../models/Cabinet";
import Page from "../../models/Page";
import { buttonItems, consumerItems, permanentPowerItems, sensorItems } from "../Toolbar";
import { showErrToast, showToast } from "../Toasts";
import DrawingItemTemplate, { DrawingItemCategory } from "../../models/DrawingItemTemplate";
import Consumer from "../../models/Consumer";
import ZipStorageHandler from "../../persistence/ZipStorageHandler";
import WaitDialog from "../WaitDialog";
import ConnectableObject from "../../models/ConnectableObject";
import AllowedConections from "../../models/AllowedConnections";
import TerminalStrip from "../../models/TerminalStrip";
import Terminal from "../../models/Terminal";
import Actor from "../../models/Actor";
import Channel from "../../models/Channel";
import { DeviceClasses } from "../../models/DeviceClasses";
import { count } from "console";
import { floor } from "three/src/nodes/TSL.js";

const logger = new Logger();

type RowData = {
  template: DrawingItemTemplate | undefined;
  tag: string;
  name: string;
  count: number;
  cable: string;
  room: string;
  floor: string;
  via: string;
  terminal: string;
  cabinet: string;
  actorChannel: string;
  consumerOrButton: ConnectableObject | undefined;
};

const columnDefinition_Type = {
  type: "type",
  name: () => {
    return itx("type", "Type");
  },
  number: -1,
};
const columnDefinition_Tag = {
  type: "tag",
  name: () => {
    return itx("tag", "Kennzeichen");
  },
  number: -1,
};
const columnDefinition_Name = {
  type: "name",
  name: () => {
    return itx("name", "Name");
  },
  number: -1,
};
const columnDefinition_Count = {
  type: "count",
  name: () => {
    return itx("csvUploadDialog.count", "Anzahl");
  },
  number: -1,
};
const columnDefinition_Cable = {
  type: "cable",
  name: () => {
    return itx("components.Cable", "Kabel");
  },
  number: -1,
};
const columnDefinition_Room = {
  type: "room",
  name: () => {
    return itx("components.Room", "Raum");
  },
  number: -1,
};
const columnDefinition_Floor = {
  type: "floor",
  name: () => {
    return itx("components.Floor", "Etage");
  },
  number: -1,
};

const columnDefinition_Via = {
  type: "via",
  name: () => {
    return itx("components.Via", "Via");
  },
  number: -1,
};
const columnDefinition_Terminal = {
  type: "terminal",
  name: () => {
    return itx("components.Terminal", "Terminal");
  },
  number: -1,
};
const columnDefinition_Cabinet = {
  type: "cabinet",
  name: () => {
    return itx("components.Cabinet", "Schaltschrank");
  },
  number: -1,
};

const columnDefinition_ActorChannel = {
  type: "actorChannel",
  name: () => {
    return itx("components.ActorChannel", "Kanal");
  },
  number: -1,
};

const columnDefinitions = [
  columnDefinition_Type,
  columnDefinition_Tag,
  columnDefinition_Name,
  columnDefinition_Count,
  columnDefinition_Cable,
  columnDefinition_Room,
  columnDefinition_Floor,
  columnDefinition_Via,
  columnDefinition_Terminal,
  columnDefinition_Cabinet,
  columnDefinition_ActorChannel,
];

export default function CsvUploadDialog(props: {}) {
  const [rows, setRows] = useState(new Array<RowData>());

  const [table, setTable] = useState("");
  const [createNewCabinetForEachFloor, setCreateNewCabinetForEachFloor] = useState(false);

  const errorValues = {
    type: itx("errorValues.type", "no type found"),
    tag: itx("errorValues.tag", "no tag found"),
    tagToLong: itx("errorValues.tagToLong", "max 4 Zeichen erlaubt"),
    name: itx("errorValues.name", "no name found"),
    count: itx("errorValues.count", "no count found"),
    cable: itx("errorValues.cable", "no cable found"),
    room: itx("errorValues.room", "no room found"),
    floor: itx("errorValues.floor", "no floor found"),
    via: itx("errorValues.via", "via not found"),
    terminal: itx("errorValues.terminal", "terminal not found"),
    cabinet: itx("errorValues.cabinet", "cabinet not found"),
    actorChannel: itx("errorValues.actorChannel", "actor channel not found"),
  };
  const channelTypeTextCombination: Array<{ type: ChannelType; name: string; img?: string }> = [
    {
      type: ChannelType.Switch,
      name: itx("components.SwitchableLight", "schaltbare Leuchte"),
      img: getChannelTypePicture(ChannelType.Switch),
    },
    // has to be bevore "schaltbare steckdose"
    {
      type: ChannelType.plug_230V,
      name: itx("components.Outlet", "Steckdose"),
      img: state.hostBaseUrl + "Plug.png",
    },
    {
      type: ChannelType.plug_Switch,
      name: itx("components.SwitchableOutlet", "schaltbare Steckdose"),
      img: state.hostBaseUrl + "PlugSwitchable.png",
    },
    {
      type: ChannelType.Dimmer,
      name: itx("components.DimmableLight", "dimmbare Leuchte"),
    },
    {
      type: ChannelType.TuneableWhite,
      name: itx("components.tw", "Tuneable White"),
    },
    {
      type: ChannelType.StaircaseLight,
      name: itx("components.StaircaseLight", "StaircaseLight"),
    },
    {
      type: ChannelType.Rgb,
      name: itx("components.Rgb", "rgb Leuchte"),
    },
    {
      type: ChannelType.Heating,
      name: itx("components.Heating", "Heizung"),
    },
    {
      type: ChannelType.Blind,
      name: itx("components.Blind", "Rolladen"),
    },
    {
      type: ChannelType.Jalousie,
      name: itx("components.Jalousie", "Jalousie"),
    },

    {
      type: ChannelType.suply230V,
      name: "230V",
      img: state.hostBaseUrl + "200V.png", // no itx
    },
    {
      type: ChannelType.suply400V,
      name: "400V",
      img: state.hostBaseUrl + "400V.png", // no itx
    },
    {
      type: ChannelType.plug_LAN,
      name: "LAN", // no itx
      img: state.hostBaseUrl + "lan.png", // no itx
    },
    {
      type: ChannelType.Lock,
      name: itx("components.Lock", "Lock"),
    },
    {
      type: ChannelType.plug_LAN,
      name: itx("components.Lan", "LAN"),
    },
    {
      type: ChannelType.plug_LAN2,
      name: itx("components.Lan2", "Duplex LAN"),
    },
    {
      type: ChannelType.Unknown,
      name: itx("components.Button", "Taster"),
    },
    {
      type: ChannelType.Unknown,
      name: itx("components.Sensor", "Sensor"),
    },
  ];

  /////////////////////////////////////////////////
  // example table
  ////////////////////////////////////////////////
  let exampleTableData = `${itx("components.Floor", "Etage")};${itx("components.Room", "Raum")};${itx("type", "Type")};${itx("tag", "Kennzeichen")};${itx("name", "Name")};${itx("csvUploadDialog.count", "Anzahl")};${itx("components.Cable", "Kabel")};${itx("components.Via", "Via")};${itx("components.Terminal", "Klemme")};${itx("components.Cabinet", "Schaltschrank")};${itx("components.ActorChannel", "Kanal")}\r\n`;
  exampleTableData =
    exampleTableData +
    `EG;Wohnzimmer;${itx("components.SwitchableLight", "schaltbare Leuchte")};L;Wandlampe;1;5x1,5\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Blind", "Rolladen")};R;Rollladen;2;5x1,5\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.Jalousie", "Jalousie")};J;Jalousie;1;5x1,5;;X1:1,2;C1\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Heating", "Heizung")};H;Heizung;1;3x1,5\r\n`;
  exampleTableData =
    exampleTableData +
    `EG;Wohnzimmer;${itx("components.DimmableLight", "dimmbare Leuchte")};L2;Deckenleuchte dimmbar;1;5x1,5\r\n`;
  exampleTableData =
    exampleTableData +
    `EG;Wohnzimmer;${itx("components.tw", "Tuneable White")};L3;Wandleuchte Tuneable White;1;5x1,5\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Sensor", "Sensor")};B;Bwegungsmelder\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.Outlet", "Steckdose")};ST;Steckdose;5;5x2,5\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.230V", "230V")};V23;Verbraucher 230V;5;5x2,5\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.400V", "400V")};V40;Verbraucher 400V;5;5x5\r\n`;
  exampleTableData =
    exampleTableData +
    `EG;Wohnzimmer;${itx("components.SwitchableOutlet", "schaltbare Steckdose")};SST;schaltbare Steckdose;5;5x2,5;L\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.Rgb", "rgb Leuchte")};LR;rgb beleuchtung;1;5x2,5;;;;A1:1\r\n`;
  exampleTableData =
    exampleTableData +
    `EG;Wohnzimmer;${itx("components.StaircaseLight", "StaircaseLight")};LT;Treppenlicht;1;3x2,5\r\n`;
  exampleTableData =
    exampleTableData + `EG;Wohnzimmer;${itx("components.Lock", "input lock")};FK;Fensterkontakt;1;5x2,5\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Lan", "LAN")};LAN;LAN;1;\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Lan2", "Duplex LAN")};LAN2;Duplex LAN;1;\r\n`;
  exampleTableData = exampleTableData + `EG;Wohnzimmer;${itx("components.Button", "Taster")};T;Taster;;`;
  ////////////////////////////////////////////////
  useEffect(() => {
    // show example Table as default
    if (table == "") {
      setTable(exampleTableData);
    }
    setRows(calcRows());
  }, []);
  //////////////////////////////////////////////
  useEffect(() => {
    // get rows
    setRows(calcRows());
  }, [table]);

  function DownloadExampleTable(props: {}) {
    async function downloadTable() {
      try {
        const csvTableName = itx("csvUploadDialog.exmpleCsvTabel") + ".csv";
        const z = new ZipStorageHandler();
        const blob = new Blob([exampleTableData], { type: "text/json" });
        const isValidZip = await z.setBlob(blob);

        const elem = window.document.createElement("a");
        elem.href = window.URL.createObjectURL(blob);
        elem.download = csvTableName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);

        showToast({
          text: state.translate("homePage.projectDownloaded", `T1 wurde auf Ihren Rechner herunter geladen`, {
            T1: csvTableName,
          }),
        });
      } catch (ex) {
        logger.log("", ex);
      }
    }
    return (
      <div style={{ display: "flex", flexDirection: "row-reverse" }}>
        <button
          className="button"
          onClick={(ev) => {
            downloadTable();
          }}
        >
          {itx("csvUploadDialog.downloadExampleTable", "Beispiel Tabelle herrunterladen")}
        </button>
      </div>
    );
  }

  function splitRowIntoColums(row: string): Array<string> {
    //
    // split one row into columns
    //
    let columns = row.split(";");
    // if not split with ";"
    if (columns.length == 1) {
      // check here if ter is
      columns = new Array<string>();
      let elem = row;
      while (elem.indexOf(",") != -1) {
        const indexKomma = elem.indexOf(",");
        const indexGaenzefuesle = elem.indexOf(`"`);
        if (indexKomma > indexGaenzefuesle && indexGaenzefuesle != -1) {
          const lastIndexGaenzefuesle = elem.indexOf(`"`, indexGaenzefuesle + 1);
          columns.push(elem.substring(indexGaenzefuesle + 1, lastIndexGaenzefuesle));
          elem = elem.substring(lastIndexGaenzefuesle + 1);
        } else {
          columns.push(elem.substring(0, indexKomma));
          elem = elem.substring(indexKomma + 1);
          if (elem.indexOf(",") == -1) {
            columns.push(elem);
          }
        }
      }
      // components = row.split(",");
    }

    return columns;
  }

  function calcRows(): Array<RowData> {
    function cleanString(value: string, key: string): string {
      if (!value) {
        return "";
      }
      let res = value.trim();
      switch (key) {
        case "tag":
        case "via": {
          res = res.replace(/[^a-zA-Z0-9]/g, "");
          break;
        }
        case "actorChannel": {
          // it has two different part the first part is a tag so it should be only letters and numbers and the second part is a number so it should be only numbers
          const resArray = res.split(":");
          if (resArray.length == 2) {
            resArray[0] = resArray[0].replace(/[^a-zA-Z0-9]/g, "");
            resArray[1] = resArray[1].replace(/[^0-9]/g, "");
            res = resArray.join(":");
          }
          break;
        }
        // count: itx("errorValues.count", "no count found"),        // cable: itx("errorValues.cable", "no cable found"),
        // room: itx("errorValues.room", "no room found"),
        // floor: itx("errorValues.floor", "no floor found"),
        // terminal: itx("errorValues.terminal", "terminal not found"),
        // cabinet: itx("errorValues.cabinet", "cabinet not found"),
        // actorChannel: itx("errorValues.actorChannel", "actor channel not found"),
      }
      return res;
    }
    const rowsCalced = new Array<RowData>();
    logger.log("calcRows inside");
    const csvRows = table.split("\r\n");
    let headerRowFound = false;
    let maxColumnsIndex = -1;

    //
    // Clear column numbers
    //

    columnDefinitions.forEach((columnDefinition) => (columnDefinition.number = -1));

    //
    // search & process Header
    //
    let r = 0;
    for (; r < Math.min(3, csvRows.length) && !headerRowFound; r++) {
      const row = csvRows[r];

      //
      // split one row into columns
      //
      const columns = splitRowIntoColums(row);

      // check the Order of the items
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        const text = columns[columnIndex];
        const columnDefinition = columnDefinitions.find((cd) => text && text.toLowerCase() == cd.name().toLowerCase());
        if (columnDefinition) {
          columnDefinition.number = columnIndex;
          maxColumnsIndex = Math.max(columnIndex, maxColumnsIndex);
          headerRowFound = true;
        } else {
          logger.error("no ColumnDefinition found for: ", text);
        }
      }
    }

    if (!headerRowFound && table != "") {
      showErrToast(
        itx("csvUpload.foundNoHeader", "keine überschrift gefunden") +
          itx("CsvUploadDialog.nzeile1", "\n Zeile1:") +
          csvRows[0],
      );
      logger.error("no header \n" + csvRows.join("\n"));
      return [];
    }

    //
    // Process data
    //

    for (; r < csvRows.length; r++) {
      const row = csvRows[r];
      const columns = splitRowIntoColums(row);

      // check the Order of the items

      // sort here the other data in table and check if values right( for example the channeltype if the count is a number etc. )
      let template: DrawingItemTemplate | undefined;

      channelTypeTextCombination.forEach((value) => {
        if (
          columnDefinition_Type.number >= 0 &&
          columns[columnDefinition_Type.number]?.toLowerCase().includes(value.name.toLowerCase())
        ) {
          consumerItems.concat(permanentPowerItems).forEach((item) => {
            if (item.type == value.type) {
              if (value.img && item.image.includes(value.img)) {
                template = item;
              } else if (value.img == undefined) {
                template = item;
              }
            }
          });
          buttonItems.forEach((item) => {
            if (value.name == itx("components.Button", "Taster") && item.classification == DeviceClasses.Button) {
              template = item;
            } else if (
              value.name == itx("components.DecentralActor", "Dezentrale Akteure") &&
              item.classification == DeviceClasses.DecentralActor
            ) {
              template = item;
            }
          });
          sensorItems.forEach((item) => {
            if (
              value.name == itx(`components.SensorNew`, "Sensor") &&
              item.classification == DeviceClasses.SensorNew &&
              value.name == item.titleShort
            ) {
              template = item;
            } else if (value.type == item.type && value.type == ChannelType.Lock) {
              template = item;
            }
          });
        }
      });
      /// get here other channeltype from name or tag

      const tag = cleanString(columns[columnDefinition_Tag.number], "tag") || "";
      const name = cleanString(columns[columnDefinition_Name.number], "name") || "";
      const cable = cleanString(columns[columnDefinition_Cable.number], "cable") || "";
      const room = cleanString(columns[columnDefinition_Room.number], "room") || "";
      const floor = cleanString(columns[columnDefinition_Floor.number], "floor") || "";
      const count = parseInt(columns[columnDefinition_Count.number] || "1");
      const via = cleanString(columns[columnDefinition_Via.number], "via") || "";
      const terminal = cleanString(columns[columnDefinition_Terminal.number], "terminal") || "";
      const cabinet = cleanString(columns[columnDefinition_Cabinet.number], "cabinet") || "";
      const actorChannel = cleanString(columns[columnDefinition_ActorChannel.number], "actorChannel") || "";

      if (tag != "" || name != "") {
        logger.log("tag or and name availiable", tag, name, columns);
        rowsCalced.push({
          tag: tag,
          name: name,
          cable: cable,
          room: room,
          floor: floor,
          template: template,
          count: count,
          via: via,
          terminal: terminal,
          cabinet: cabinet,
          consumerOrButton: undefined,
          actorChannel: actorChannel,
        });
        logger.log("middle calculated rows", rowsCalced);
      } else {
        logger.error("no tag or name availiable:", columnDefinitions);
      }
    }

    logger.log("calculated rows", rowsCalced);
    return rowsCalced;
  }

  function UploadTable(props: {}) {
    function LoadedData(props: {}) {
      if (table == "") {
        return null;
      }
      /// get here the projectStructure

      const rowViews = new Array<any>();
      function ErrorWarning(props: { tooltipText: string; text?: string }) {
        const { tooltipText, text } = props;
        return (
          <div
            data-tooltip-id="showhide-tooltip" // no itx
            data-tooltip-content={tooltipText}
            data-tooltip-place="bottom"
          >
            {text}
            <img
              src={"images/warning.png"}
              style={{
                width: "25px",
                height: "25px",
                position: "relative",
              }}
              data-tooltip-id="showhide-tooltip" // no itx
              data-tooltip-content={tooltipText}
              data-tooltip-place="bottom"
            />
            <span style={{ fontSize: "10px" }}>{tooltipText || itx("csvUploadDialog.notFound", "not found")}</span>
          </div>
        );
      }

      // draw rows
      rows.forEach((row) => {
        let typeName = itx(`channelType.${row.template?.type}`);
        if (row.template?.category == DrawingItemCategory.Button) {
          typeName = row.template.titleShort;
        }
        const typeView = (
          <div>
            <img
              src={row.template?.image}
              style={{ width: "20px", height: "20px", objectFit: "contain", marginRight: "5px" }}
            />
            {typeName}
          </div>
        );

        const columnStyle = { borderRight: "1px solid lightgrey", padding: "5px 10px" }; // no itx
        let cableShow: any = row.cable;
        if (row.template?.category != DrawingItemCategory.Button && row.cable == "") {
          cableShow = <ErrorWarning tooltipText={errorValues.cable} />;
        } else if (row.template?.category == DrawingItemCategory.Button) {
          cableShow = "-";
        }

        let terminalInput = row.terminal || "-";
        if (terminalInput != "-" && !terminalInput.split(":")[1]) {
          terminalInput = itx("csvUploadDialog.wrongSyntax", "Falsche Syntax: z.B. X1:1,2");
        }
        let tagElement: any = row.tag;
        if (row.tag.length > 4) {
          tagElement = <ErrorWarning tooltipText={errorValues.tagToLong} text={row.tag} />;
        } else if (row.tag == "" || !row.tag) {
          tagElement = <ErrorWarning tooltipText={errorValues.tag} />;
        }
        let actorChannel = row.actorChannel || "-";
        if (
          actorChannel != "-" &&
          (actorChannel.split(":").length != 2 || Number.isNaN(parseInt(actorChannel.split(":")[1])))
        ) {
          actorChannel += " " + itx("csvUploadDialog.wrongSyntaxActorChannel", "Falsche Syntax: z.B. A1:1");
        }

        rowViews.push(
          <tr
            style={{ borderBottom: rowViews.length >= rows.length - 1 ? undefined : "1px solid grey" }} // no itx
          >
            <td style={columnStyle}>{row.floor || <ErrorWarning tooltipText={errorValues.floor} />}</td>
            <td style={columnStyle}>{row.room || <ErrorWarning tooltipText={errorValues.room} />}</td>
            <td style={columnStyle}>{row.template ? typeView : <ErrorWarning tooltipText={errorValues.type} />}</td>
            <td style={columnStyle}>{tagElement}</td>
            <td style={columnStyle}>{row.name || <ErrorWarning tooltipText={errorValues.name} />}</td>
            <td style={columnStyle}>
              {Number.isNaN(row.count) ? <ErrorWarning tooltipText={errorValues.count} /> : row.count}
            </td>
            <td style={{ ...columnStyle, borderRight: "0px" }}>{cableShow}</td>
            <td style={{ ...columnStyle, borderRight: "0px" }}>{row.via || "-"}</td>
            <td style={{ ...columnStyle, borderRight: "0px" }}>{terminalInput}</td>
            <td style={{ ...columnStyle, borderRight: "0px" }}>{row.cabinet || "-"}</td>
            <td style={{ ...columnStyle, borderRight: "0px" }}>{actorChannel}</td>
          </tr>,
        );
      });

      const headerColumnStyle: any = { borderLeft: "1px solid grey", padding: "5px 10px", textAlign: "center" }; // no itx
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexDirection: "row", padding: "5px 0" }}>
            <input
              id="createNewCabinetForEachFloor"
              type="checkbox"
              onClick={() => {
                setCreateNewCabinetForEachFloor(!createNewCabinetForEachFloor);
              }}
              checked={createNewCabinetForEachFloor}
            />
            <div>{itx("csvUploadDialog.createCabinetPerFloor", "Pro Etage einen Schaltschrank anlegen")}</div>
          </div>
          <div style={{ borderRadius: "10px", overflow: "hidden", height: "max-content", border: " 1px solid grey" }}>
            <table style={{}}>
              <thead style={{ backgroundColor: "lightgray", fontWeight: "bold" }}>
                <tr>
                  <td></td>
                  <td style={headerColumnStyle}></td>
                  <td colSpan={4} style={{ ...headerColumnStyle, textAlign: "center" }}>
                    {itx("components.plural.Consumer", "Verbraucher")}
                  </td>
                  <td colSpan={5} style={headerColumnStyle}></td>
                </tr>
                <tr>
                  <td style={{ ...headerColumnStyle, borderLeft: "0" }}>{itx("components.Floor", "Etage")}</td>
                  <td style={headerColumnStyle}>{itx("components.Room", "Raum")}</td>
                  <td style={{ borderLeft: "1px solid grey" }}>{itx("type", "Type")}</td>
                  <td style={headerColumnStyle}>{itx("tag", "Kennzeichen")}</td>
                  <td style={headerColumnStyle}>{itx("name", "Name")}</td>
                  <td style={headerColumnStyle}>{itx("count", "Anzahl")}</td>
                  <td style={headerColumnStyle}>{itx("components.Cable", "Kabel")}</td>
                  <td style={headerColumnStyle}>{itx("components.Via", "Via")}</td>
                  <td style={headerColumnStyle}>{itx("components.Terminal", "Terminal")}</td>
                  <td style={{ ...headerColumnStyle }}>{itx("components.Cabinet", "Schaltschrank")}</td>
                  <td style={headerColumnStyle}>{itx("components.ActorChannel", "Kanal")}</td>
                </tr>
              </thead>
              <tbody>{rowViews}</tbody>
            </table>
          </div>
        </div>
      );
    }

    function DownloadCsvButton(props: {}) {
      function downloadCsv(e: React.ChangeEvent<HTMLInputElement>) {
        logger.log("event target", e.target);
        if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];

          logger.log("importet file:", file);
          const reader = new FileReader();
          reader.readAsText(file);

          reader.onloadend = () => {
            // convert image file to base64 string
            const text = reader.result;
            if (text && text.toString().indexOf("�") != -1) {
              const newReader = new FileReader();
              newReader.readAsText(file, "ISO-8859-1"); // no itx
              newReader.onloadend = () => {
                // convert image file to base64 string
                const text = newReader.result;
                if (text && text.toString().indexOf("�") != -1) {
                  showErrToast(
                    itx(
                      "csvUpload.wrongEncoding",
                      "Die Datei hat verfälschte Sonderzeichen. Bitte als UTF-8 kodierte Datei abspeichern.",
                    ),
                  );
                } else {
                  if (typeof text == "string") {
                    setTable(text);
                    logger.log("importet table is text", text);
                  } else {
                    logger.error("importet table is no text", text);
                  }
                }
              };
            } else {
              if (typeof text == "string") {
                setTable(text);
                logger.log("importet table is text", text);
              } else {
                logger.error("importet table is no text", text);
              }
            }
          };
        }
      }

      return (
        <label
          className={"selectFileUploadCsv"} // no itx
        >
          <input
            style={{ display: "none" }}
            id="file"
            name="file"
            type="file"
            accept=".csv" // no itx
            onClick={(event) => {
              event.stopPropagation;
            }}
            onChange={(event) => {
              downloadCsv(event);
            }}
          />
          <i className="las la-upload" style={{ fontSize: "32px", paddingTop: "6px" }} />
          <br />
          {table
            ? itx("csvUploadDialog.updateFile", "update File")
            : state.translate("ShowRoomSubmittalsBackground.selectFile", "Datei auswählen")}
        </label>
      );
    }
    let possibleChannelTypes = "";

    channelTypeTextCombination.forEach((value) => {
      if (possibleChannelTypes != "") {
        possibleChannelTypes = possibleChannelTypes + ", ";
      }
      possibleChannelTypes = possibleChannelTypes + value.name;
    });
    const headers =
      columnDefinition_Floor.name +
      ", " +
      columnDefinition_Room.name +
      ", " +
      columnDefinition_Type.name +
      ", " +
      columnDefinition_Tag.name +
      ", " +
      columnDefinition_Name.name +
      ", " +
      columnDefinition_Count.name +
      ", " +
      columnDefinition_Cable.name +
      ", " +
      columnDefinition_Via.name;
    ", " +
      columnDefinition_Terminal.name +
      ", " +
      columnDefinition_Cabinet.name +
      "," +
      columnDefinition_ActorChannel.name;

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid lightgrey",
            padding: "5px",
            borderRadius: "15px",
            marginBottom: "15px",
          }}
        >
          <div style={{ alignSelf: "center" }}>
            {itx("csvUploadDialog.uploadTable", "Hier kannste du deine Csv-Tabelle hochladen.")}
            <br />
          </div>

          <DownloadCsvButton />
          {table == "" && (
            <div
              style={{
                alignSelf: "center",
                maxWidth: "500px",
                display: "flex",
                textAlign: "center",
                fontSize: "12px",
                flexDirection: "column",
              }}
            >
              {itx("csvUploadDialog.followingHeaders", "Bitte setzen sie folgende Überschriften:")}
              <br />
              {headers}
              <br />
              <br />
              {itx("csvUploadDialog.possybleConsumerTypes", "Mögliche Verbraucher-Typen sind:")}
              <br />
              {possibleChannelTypes}
              <br />
              {itx("eg", "z.B.") + ":"}
              <div style={{ border: "1px solid black", borderRadius: "10px" }}>
                <table>
                  <thead style={{ fontWeight: "bold", color: "grey", borderBottom: "1px solid grey" }}>
                    <td>{columnDefinition_Floor.name()} </td>
                    <td>{columnDefinition_Room.name()} </td>
                    <td>{columnDefinition_Type.name()} </td>
                    <td>{columnDefinition_Tag.name()} </td>
                    <td>{columnDefinition_Name.name()} </td>
                    <td>{columnDefinition_Count.name()} </td>
                    <td>{columnDefinition_Cable.name()} </td>
                    <td>{columnDefinition_Via.name()} </td>
                    <td>{columnDefinition_Terminal.name()} </td>
                    <td>{columnDefinition_Cabinet.name()} </td>
                    <td>{columnDefinition_ActorChannel.name()} </td>
                  </thead>

                  <tbody>
                    <tr>
                      <td>{itx("CsvUploadDialog.eg", "EG")} </td>
                      <td>{itx("CsvUploadDialog.wz", "WZ")} </td>
                      <td>{itx("components.SwitchableLight", "schaltbare Leuchte")} </td>
                      <td>D </td>
                      <td>{itx("CsvUploadDialog.deckenspots", "Deckenspots ")}</td>
                      <td>{"4"} </td>
                      <td>3x2,5{/* no itx */}</td>
                    </tr>

                    <tr>
                      <td colSpan={7} style={{ textAlign: "center" }}>
                        ...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <LoadedData />
      </div>
    );
  }
  async function createConsumers() {
    const errors = new Map<string, any>();
    let createdConsumers = 0;
    let createdSensors = 0;
    let createdButtons = 0;
    let createdDecentralActors = 0;
    function createCabinet(floorName: string): string {
      let cabinetId = "";
      let name = "";
      let tag = "";
      if (state.cabinets.size > 0 || createNewCabinetForEachFloor) {
        name = itx("components.Cabinet") + " " + floorName;
        tag = itx("components.Cabinet").substring(0, 1) + floorName.substring(0, 1);
      }
      const cabinet = new Cabinet(
        {
          name: name,
        },
        state.cabinets,
      );
      cabinet.tag = tag || state.nameAndTagManager.generateTag(cabinet);
      state.cabinets.add(cabinet);
      cabinetId = cabinet.id;
      return cabinetId;
    }

    async function handleActorChannel(props: {
      consumer: Consumer;
      actorChannel: string;
      cabinet: Cabinet | undefined;
    }): Promise<Consumer | undefined> {
      const { consumer, actorChannel, cabinet } = props;
      const room = consumer.parent as Room;

      // example A3:1 A3 aktor, 1 nummer of channel
      const channelParts = actorChannel.split(":");
      if (channelParts.length == 2) {
        const actorTag = channelParts[0];
        const channelNumber = parseInt(channelParts[1]);
        if (channelNumber >= 1 && !Number.isNaN(channelNumber)) {
          let actor: Actor | undefined = undefined;
          state.allActors().forEach((a) => {
            if (a.tag.toLowerCase() == actorTag.toLowerCase()) {
              actor = a;
            }
          });
          if (!actor) {
            // create actor if not found
            let parentCabinet = cabinet || (consumer.parent as Room)?.cabinet || state.cabinets[0] || undefined;
            if (!parentCabinet) {
              // create main cabinet
              const mainCabinet = new Cabinet(
                {
                  name: "",
                },
                state.cabinets,
              );
              mainCabinet.tag = "";
              state.cabinets.add(mainCabinet);
              parentCabinet = mainCabinet;
            }
            // now create actor
            actor = (parentCabinet as Cabinet).createActor({ tag: actorTag });
            actor.tag = actorTag;

            parentCabinet.actors.add(actor);
          }
          if (actor) {
            // channel numberstring has to be 2 characters long, so if the channel number is 1, it has to be 01

            const channelNumberString = channelNumber.toString().padStart(2, "0");

            let channel: Channel | undefined =
              actor.channels.asArray().filter((c) => c.tag == channelNumberString)[0] ||
              actor.channels.sortBy("tagOrName")[channelNumber - 1];
            if (!channel) {
              const toCreateChannels = channelNumber - actor.channels.size;
              for (let c = 0; c < toCreateChannels; c++) {
                const newChannel = new Channel({ tag: (actor.channels.size + 1).toString() }, actor);
                newChannel.tag = (actor.channels.size + 1).toString();
                newChannel.name = ` ${itx("channelType.Channel", "Kanal")} ${newChannel.tag}`;
                newChannel.channelType = consumer.channelType;

                actor.channels.add(newChannel);
              }
              channel = actor.channels.asArray()[channelNumber - 1];
            }
            if (channel && room.page) {
              if (channel.connections.size == 0) {
                // bulk import: don't prompt to sync the consumer's channelType with the actor channel
                const connection = channel.connectConsumer(room.page, consumer, false);
                if (!connection) {
                  const creationsErrorsAllreadyIn = errors.get("couldNotConnectChannel") || [];
                  creationsErrorsAllreadyIn.push(
                    itx(
                      "csvUploadDialog.couldNotConnectChannelText",
                      "Der Kanal {T1} vom Aktor {T2} konnte nicht mit dem Verbraucher {T3} verbunden werden.",
                      {
                        T1: channel.tag,
                        T2: actor.tag,
                        T3: consumer.name,
                      },
                    ),
                  );
                  errors.set("couldNotConnectChannel", creationsErrorsAllreadyIn);
                }
              } else {
                const channelInUseErrorsAlreadyIn = errors.get("channelInUse") || [];
                channelInUseErrorsAlreadyIn.push(
                  itx("csvUploadDialog.channelInUseText", "Der Kanal {T1} vom Aktor {T2} wird bereits verwendet.", {
                    T1: channel.tag,
                    T2: actor.tag,
                  }),
                );
                errors.set("channelInUse", channelInUseErrorsAlreadyIn);
              }
            }
          }
        }
      } else {
        const wrongActorChannelSyntaxErrorsAlreadyIn = errors.get("wrongActorChannelSyntax") || [];
        wrongActorChannelSyntaxErrorsAlreadyIn.push(
          itx(
            "csvUploadDialog.wrongActorChannelSyntaxText",
            "Die Angabe {T1} für den Kanal ist falsch. Bitte folgendes Format verwenden: A3:1 (A3=Aktortag, 1=Kanalnummer).",
            { T1: actorChannel },
          ),
        );
        errors.set("wrongActorChannelSyntax", wrongActorChannelSyntaxErrorsAlreadyIn);
      }

      return consumer;
    }
    async function createConsumer(props: {
      room: Room | undefined;
      tag: string;
      name: string;
      image: string;
      type: ChannelType | "button" | "sensor" | "decentralActor";
      count: number;
      cable: string;
      terminal: string;
      actorChannel: string;
      cabinet?: string;
    }): Promise<ConnectableObject | undefined> {
      const { room, tag, name, image, type, count, cable, terminal, actorChannel, cabinet: cabinetTag } = props;
      const page = room?.page;
      if (page && room && count >= 1) {
        const pPX = page.findFreePositionForNewObject(
          page.getBeginnPointForConsumerFromChannelType(
            type != "button" && type != "sensor" && type != "decentralActor" ? type : undefined,
          ),
        );

        // check here if cabinet is given and search or create new cabinet
        let cabinet: Cabinet | undefined;
        if (props.cabinet && props.cabinet != "") {
          state.cabinets.forEach((c) => {
            if (
              c.name.toLowerCase() == props.cabinet?.toLowerCase() ||
              c.tag.toLowerCase() == props.cabinet?.toLowerCase()
            ) {
              cabinet = c;
            }
          });
          if (!cabinet) {
            // create new main cabinet
            const newCabinet = new Cabinet({ tag: cabinetTag }, state.cabinets);
            state.cabinets.add(newCabinet);
            cabinet = newCabinet;
          }
        }

        const consumerOrButton =
          type == "button"
            ? await page.createNewButtonInPage(room, { tag: tag, name: name, image: image }, pPX)
            : await page.createNewConsumerInPage(
                room,
                { tag: tag, name: name, image: image, type: type },
                pPX,
                cabinet,
                false, // no auto connect if actor channel is given,
              );

        if (consumerOrButton) {
          if (consumerOrButton.isDecentralActor) {
            createdDecentralActors++;
          } else if (consumerOrButton.isSensor) {
            createdSensors++;
          } else if (consumerOrButton.isButton) {
            createdButtons++;
          } else {
            createdConsumers++;
          }
          if (!tag) {
            consumerOrButton.tag = "0??"; // get a new tag in  // no itx
            // consumerOrButton.tag = state.nameAndTagManager.generateTag(consumerOrButton);
            // consumerOrButton.tag = consumerOrButton.tag.substring(0, state.maxCharLimit.tag);
          }
          if (tag) {
            // check first if tag is allreadyUsed
            const allreadyUsedTags = new Array<string>();
            consumerOrButton.tag = tag.toUpperCase();
            if (consumerOrButton.isButton) {
              state.allButtons().forEach((b) => {
                if (b.id != consumerOrButton.id) {
                  if ((b.isSensor || b.isButton || b.isDecentralActor) && type == "button") {
                    allreadyUsedTags.push(b.tag);
                  }
                }
              });
            } else {
              state.allConsumers().forEach((c) => {
                if (c.id != consumerOrButton.id) {
                  allreadyUsedTags.push(c.tag);
                }
              });
            }

            const newTag = state.nameAndTagManager.getAvailableTagWithBracetsIfNeeded(tag, allreadyUsedTags || []);
            consumerOrButton.tag = newTag.toUpperCase();
            // consumerOrButton.tag = consumerOrButton.tag.substring(0, state.maxCharLimit.tag);
          }

          if (name) {
            consumerOrButton.name = name;
          }

          // switchable plug
          if (image.includes("PlugSwitchable.png")) {
            (consumerOrButton as Consumer).channelType = (consumerOrButton as Consumer).prefixType = ChannelType.plug_Switch;
          }
          if (image.includes("Plug.png")) {
            (consumerOrButton as Consumer).channelType = (consumerOrButton as Consumer).prefixType = ChannelType.plug_230V;
          }

          if (type != "button" && type != "sensor" && type != "decentralActor") {
            // consumer can have this atributes -> button not
            if (count) {
              (consumerOrButton as Consumer).quantity = count;
            }
            if (cable) {
              (consumerOrButton as Consumer).setCableTypeByName(cable);
            } else {
              // default cable type if not given
            }
            if (actorChannel) {
              await handleActorChannel({
                consumer: consumerOrButton as Consumer,
                actorChannel: actorChannel,
                cabinet: cabinet,
              });
            }
          }
          return consumerOrButton;
        } else {
          return undefined;
        }
      }
    }
    try {
      // choose Cabinet for each consumer

      let i = 0;
      //let createNewCabinetForEachFloor = (document.getElementById("createNewCabinetForEachFloor") as HTMLInputElement)?.checked;

      for (const row of rows) {
        // search floor
        let floor: Floor | undefined;
        const floorName = row.floor || "{i}"; // no itx
        state.floors.forEach((f) => {
          if (
            f.tag.toLocaleLowerCase() == floorName.toLocaleLowerCase() ||
            f.name.toLocaleLowerCase() == floorName.toLocaleLowerCase()
          ) {
            floor = f;
          }
        });

        let cabinetId = floor?.cabinetId || "";

        if (!floor) {
          if (createNewCabinetForEachFloor) {
            cabinetId = createCabinet(floorName);
          } else {
            if (state.cabinets.size == 0) {
              cabinetId = createCabinet(row.floor);
            } else {
              cabinetId = state.cabinets.asArray()[0].id;
            }
          }
          floor = new Floor({ name: floorName, cabinetId: cabinetId }, state.floors);
          floor.generateTagFromName();
          state.floors?.add(floor);
          if (!row.floor) {
            floor.errorText = "not found in csv upload";
          }
        }
        // search room
        let room: Room | undefined;
        const roomName = row.room || "{i}"; // no itx
        floor?.rooms.forEach((r) => {
          if (
            r.tag.toLocaleLowerCase() == roomName.toLocaleLowerCase() ||
            r.name.toLocaleLowerCase() == roomName.toLocaleLowerCase()
          ) {
            room = r;
          }
        });
        if (!room) {
          room = new Room({ name: roomName, cabinetId: cabinetId }, floor);
          if (!room.page) {
            room.page = new Page({}, room);
          }
          room.generateTagFromName();
          floor?.rooms.add(room);
          if (!row.room) {
            room.errorText = "not found in csv upload";
          }
        }

        if (room) {
          state.selectionManager.selectedObject = room;
          // create consumer
          if (row.template) {
            const consumer = await createConsumer({
              room: room,
              tag: row.tag,
              name: row.name,
              image: row.template.image,
              type: row.template.category == DrawingItemCategory.Consumer ? row.template.type : "button",
              count: row.count,
              cable: row.cable,
              terminal: row.terminal,
              cabinet: row.cabinet,
              actorChannel: row.actorChannel,
            });
            if (consumer) {
              row.consumerOrButton = consumer;
            }
          } else {
            const consumer = await createConsumer({
              room: room,
              tag: row.tag,
              name: row.name,
              image: getChannelTypePicture(ChannelType.Generic),
              type: ChannelType.Generic,
              count: row.count,
              cable: row.cable,
              terminal: row.terminal,
              cabinet: row.cabinet,
              actorChannel: row.actorChannel,
            });
            if (consumer) {
              row.consumerOrButton = consumer;
            }
          }
        }
        if (row.consumerOrButton == undefined) {
          // element could not be created
          const creationsErrorsAllreadyIn = errors.get("notcreated") || [];

          creationsErrorsAllreadyIn.push(
            <tr>
              <td>{row.floor}</td>
              <td>{row.room}</td>
              <td>{row.tag}</td>
              <td>{row.name}</td>
            </tr>,
          );

          errors.set("notcreated", creationsErrorsAllreadyIn);

          console.error("no room found or created for row", row);
        }
        i++;
      }

      // create via and generate tags for consumers with no tag and terminal// handle here also actor channel autoConnect
      for (const row of rows) {
        const consumerFromRow = row.consumerOrButton;
        let cabinet = state.cabinets.asArray().find((c) => {
          return c.tag.toLowerCase() == row.cabinet;
        });
        if (!cabinet) {
          cabinet = (consumerFromRow?.parent?.parent as Room)?.cabinet || state.cabinets.asArray()[0];
        }
        if (consumerFromRow) {
          //////////////////////////////////////////////////////
          // autoconnect if atorchannnel is not given
          /////////////////////////////////////////////////////
          if (row.actorChannel.split(":").length != 2) {
            const consumer = consumerFromRow as Consumer;
            const page = (consumer.parent as Room)?.getOrCreatePage();

            if (page && cabinet) {
              const autoConnected = consumer.autoConnectChannel(page, cabinet);
            }
          }
          //////////////////////////////////////////////////////
          // create via connection
          //////////////////////////////////////////////////////
          if (row.via && row.via != "" && consumerFromRow.className == "Consumer") {
            const viaFromRow = state.allConsumers().filter((c) => {
              return c.tag.toLowerCase() == row.via.toLowerCase();
            })[0];
            if (
              consumerFromRow &&
              viaFromRow &&
              AllowedConections.isConnectionForTerminalViaConnectionsAllowed(consumerFromRow, viaFromRow)
            ) {
              (consumerFromRow as Consumer).setTerminalViaConsumer(viaFromRow);
            } else {
              // element could not be created
              if (!viaFromRow) {
                const creationsErrorsAllreadyIn = errors.get("viaConsumerNotFound") || [];

                creationsErrorsAllreadyIn.push(
                  <tr>
                    <td>{row.floor}</td>
                    <td>{row.room}</td>
                    <td>{row.tag}</td>
                    <td>{row.name}</td>
                    <td>{row.via}</td>
                  </tr>,
                );

                errors.set("viaConsumerNotFound", creationsErrorsAllreadyIn);
              } else {
                const creationsErrorsAllreadyIn = errors.get("viaConnectionNotAllowed") || [];

                creationsErrorsAllreadyIn.push(
                  <tr>
                    <td>{row.floor}</td>
                    <td>{row.room}</td>
                    <td>{row.tag}</td>
                    <td>{row.name}</td>
                    <td>{row.via}</td>
                  </tr>,
                );

                errors.set("viaConnectionNotAllowed", creationsErrorsAllreadyIn);
              }
            }
          }
          if (row.tag == "") {
            consumerFromRow.tag = state.nameAndTagManager.generateTag(consumerFromRow);
            consumerFromRow.tag = consumerFromRow.tag.substring(0, state.maxCharLimit.tag);
            consumerFromRow.raiseOnChange("tag");
          }
          ///////////////////////////////////////////////////////
          // create terminal connection
          //////////////////////////////////////////////////////
          if (consumerFromRow.className == "Consumer" && row.terminal && row.terminal != "") {
            // first find or create terminalStrip
            const terminalStripTag = row.terminal.split(":")[0];
            const terminalTag = row.terminal.split(":")[1];
            if (cabinet) {
              let terminalStrip = cabinet.terminalStrips.asArray().find((ts) => {
                return ts.tag.toLowerCase() == terminalStripTag.toLowerCase();
              });
              if (!terminalStrip) {
                terminalStrip = new TerminalStrip({ name: "" }, cabinet);
                terminalStrip.tag = terminalStripTag.toUpperCase();
                cabinet.terminalStrips.add(terminalStrip);
              }
              // now create terminal
              const newTerminal = new Terminal({}, terminalStrip);
              newTerminal.tag = terminalTag || state.nameAndTagManager.generateTag(newTerminal);
              terminalStrip.terminals.add(newTerminal);
              (consumerFromRow as Consumer).connectWithTerminal(newTerminal, ["differentCabinet"]); // no itx
            }
            if ((consumerFromRow as Consumer).terminal == undefined) {
              const creationsErrorsAllreadyIn = errors.get("terminalNotConnected") || [];

              creationsErrorsAllreadyIn.push(
                <tr>
                  <td>{row.floor}</td>
                  <td>{row.room}</td>
                  <td>{row.tag}</td>
                  <td>{row.name}</td>
                  <td>{row.terminal}</td>
                </tr>,
              );

              errors.set("terminalNotConnected", creationsErrorsAllreadyIn);
            }
          }
        } else {
          console.error("no consumer found for row", row);
        }
      }
      // new loop to retag consumers with to long tags because of viaConnections
      for (const row of rows) {
        // find consumer from row
        const consumerFromRow = row.consumerOrButton;

        if (consumerFromRow) {
          //////////////////////////////////////////////////////
          // retag here if tag to long
          /////////////////////////////////////////////////////
          if (row.tag.length > 4) {
            consumerFromRow.name = consumerFromRow.name + ` (${consumerFromRow.tag})`; // no itx
            consumerFromRow.tag = state.nameAndTagManager.generateTag(consumerFromRow);
            const creationsErrorsAllreadyIn = errors.get("tagTooLong") || [];

            creationsErrorsAllreadyIn.push(
              <tr>
                <td>{row.floor}</td>
                <td>{row.room}</td>
                <td>{row.tag}</td>
                <td>{row.name}</td>
                <td>{consumerFromRow.tag}</td>
              </tr>,
            );

            errors.set("tagTooLong", creationsErrorsAllreadyIn);
          }
        } else {
          console.error("no consumer found for row", row);
        }
      }
      // to show terminals correctly we have to update all consumer ralations
      for (const row of rows) {
        const consumerFromRow = row.consumerOrButton;
        consumerFromRow?.initRelations();
      }
    } catch (ex) {
      logger.error("error in createConsumers", ex);
    }
    state.eventLogger.addEvent(EventKey.CsvImported, {
      fileName: "",
      importedRows: rows.length,
    });
    state.dialogManager.openDialog(
      <ResumeDialog
        createdConsumers={createdConsumers}
        createdButtons={createdButtons}
        createdSensors={createdSensors}
        createdDecentralActors={createdDecentralActors}
        errors={errors}
      />,
    );
  }

  function ResumeDialog(props: {
    createdConsumers: number;
    createdButtons: number;
    createdSensors: number;
    createdDecentralActors: number;
    errors: Map<string, any>;
  }) {
    const { errors, createdButtons, createdConsumers, createdSensors, createdDecentralActors } = props;
    const errorElements = new Array<any>();

    function BlockElement(props: { title: string | JSX.Element; block: any }) {
      const { title, block } = props;
      return (
        <div style={{ border: "1px solid grey", borderRadius: "10px", marginTop: "10px" }}>
          <div style={{ backgroundColor: "lightgrey", padding: "5px", borderRadius: "10px 10px 0 0" }}>{title}</div>

          <div style={{ padding: "5px" }}>{block}</div>
        </div>
      );
    }
    function CreationElement(props: {}) {
      const createdFunctionsElement = new Array<any>();
      if (createdConsumers > 0) {
        createdFunctionsElement.push(
          <tr>
            <td>{itx("components.plural.Consumer", "Verbraucher")}:</td> <td>{createdConsumers}</td>
          </tr>,
        );
      }
      if (createdButtons > 0) {
        createdFunctionsElement.push(
          <tr>
            <td>{itx("components.plural.Button", "Taster")}:</td> <td>{createdButtons}</td>
          </tr>,
        );
      }
      if (createdSensors > 0) {
        createdFunctionsElement.push(
          <tr>
            <td>{itx("components.plural.Sensor", "Sensor")}:</td> <td>{createdSensors}</td>
          </tr>,
        );
      }
      if (createdDecentralActors > 0) {
        createdFunctionsElement.push(
          <tr>
            <td>{itx("components.plural.DecentralActor", "Dezentrale Akteure")}:</td> <td>{createdDecentralActors}</td>
          </tr>,
        );
      }

      if (createdFunctionsElement.length == 0) {
        return <div>{itx("csvUploadDialog.noFunctionsCreated", "Es wurden keine Funktionen erstellt.")}</div>;
      }
      return (
        <BlockElement
          title={itx("csvUploadDialog.createdFunctions", "Es wurden folgende Funktionen erstellt:")}
          block={<table>{createdFunctionsElement}</table>}
        />
      );
    }
    errors.forEach((value, key) => {
      if (key == "notcreated") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.notCreatedFunction", "Folgende Funktionen konnten nicht erstellt werden:")}
            block={
              <table>
                <thead>
                  <tr>
                    <th>{itx("components.Floor", "Etage")}</th>
                    <th>{itx("components.Room", "Raum")}</th>
                    <th>{itx("tag", "Kennzeichen")}</th>
                    <th>{itx("name", "Name")}</th>
                  </tr>
                </thead>
                <tbody>{value}</tbody>
              </table>
            }
          />,
        );
      } else if (key == "viaConsumerNotFound") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.viaConsumerNotFound", "Folgende Via Funktionen konnten nicht gefunden werden:")}
            block={
              <table>
                <thead>
                  <tr>
                    <th>{itx("components.Floor", "Etage")}</th>
                    <th>{itx("components.Room", "Raum")}</th>
                    <th>{itx("tag", "Kennzeichen")}</th>
                    <th>{itx("name", "Name")}</th>
                    <th>{itx("components.Via", "Via")}</th>
                  </tr>
                </thead>
                <tbody>{value}</tbody>
              </table>
            }
          />,
        );
      } else if (key == "viaConnectionNotAllowed") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.viaConnectionNotAllowed", "Folgende Via Verbindungen wurden nicht erlaubt:")}
            block={
              <table>
                <thead>
                  <tr>
                    <th>{itx("components.Floor", "Etage")}</th>
                    <th>{itx("components.Room", "Raum")}</th>
                    <th>{itx("tag", "Kennzeichen")}</th>
                    <th>{itx("name", "Name")}</th>
                    <th>{itx("components.Via", "Via")}</th>
                  </tr>
                </thead>
                <tbody>{value}</tbody>
              </table>
            }
          />,
        );
      } else if (key == "terminalNotConnected") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.terminalNotConnected", "Folgende Terminal Zuweisung wurden nicht erlaubt:")}
            block={
              <table>
                <thead>
                  <tr>
                    <th>{itx("components.Floor", "Etage")}</th>
                    <th>{itx("components.Room", "Raum")}</th>
                    <th>{itx("tag", "Kennzeichen")}</th>
                    <th>{itx("name", "Name")}</th>
                    <th>{itx("components.Terminal", "Terminal")}</th>
                  </tr>
                </thead>
                <tbody>{value}</tbody>
              </table>
            }
          />,
        );
      } else if (key == "tagTooLong") {
        errorElements.push(
          <BlockElement
            title={itx(
              "csvUploadDialog.tagTooLong",
              "Folgende Kennzeichen waren länger als 4 Zeichen und wurden deshalb umbenannt:",
            )}
            block={
              <table>
                <thead>
                  <tr>
                    <th>{itx("components.Floor", "Etage")}</th>
                    <th>{itx("components.Room", "Raum")}</th>
                    <th>{itx("tag", "Kennzeichen")}</th>
                    <th>{itx("name", "Name")}</th>
                    <th>{itx("csvUploadDialog.generatedTag", "Erzeugtes Kennzeichen")}</th>
                  </tr>
                </thead>
                <tbody>{value}</tbody>
              </table>
            }
          />,
        );
      } else if (key == "couldNotConnectChannel") {
        errorElements.push(
          <BlockElement
            title={itx(
              "csvUploadDialog.couldNotConnectChannel",
              "Folgende Kanäle konnten nicht mit den Verbrauchern verbunden werden:",
            )}
            block={<div>{value}</div>}
          />,
        );
      } else if (key == "channelInUse") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.channelInUse", "Folgende Kanäle werden bereits verwendet:")}
            block={<div>{value}</div>}
          />,
        );
      } else if (key == "wrongActorChannelSyntax") {
        errorElements.push(
          <BlockElement
            title={itx("csvUploadDialog.wrongActorChannelSyntax", "Folgende Angaben für Aktorkanäle sind fehlerhaft:")}
            block={<div>{value}</div>}
          />,
        );
      }
    });

    return (
      <BaseDialog
        title={itx("csvUploadDialog.uploadComplete", "Erstellung abgeschlossen")}
        onOk={() => {
          return true;
        }}
        noAbortButton={true}
      >
        <CreationElement />

        {errorElements.length > 0 ? (
          <BlockElement
            title={
              <div>
                <img
                  src={"images/warning.png"}
                  style={{
                    width: "15px",
                    height: "15px",
                    position: "relative",
                    marginRight: "5px",
                  }}
                />
                {itx(
                  "csvUploadDialog.followingErrorsOccured",
                  "Beim Erstellen der Funktionen sind folgende Fehler aufgetreten:",
                )}
              </div>
            }
            block={<div style={{ marginTop: "10px" }}>{errorElements}</div>}
          />
        ) : (
          <div>{itx("csvUploadDialog.allFunctionsCreated", "Alle Funktionen wurden erfolgreich erstellt.")}</div>
        )}
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      title={itx("csvUploadDialog.uploadCsv", "uploadCsv")}
      onOk={() => {
        createConsumers();
        state.dialogManager.openDialog(
          <WaitDialog
            title={itx("step5.somePatience", "Bitte haben Sie etwas Geduld.")}
            text={itx("csvUploadDialog.createConsumersAndConnect", "Funktionen und deren Verbindungen werden erstellt")}
          />,
        );

        return false;
      }}
      noOkButton={rows.length < 1}
      okCaption={itx("csvUploadDialog.createFunctions", "Funktionen erstellen")}
    >
      <UploadTable />
      <DownloadExampleTable />
    </BaseDialog>
  );
}
